import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { TimeField } from '@mui/x-date-pickers/TimeField';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import Loading from '../../components/loading';
import ModalErrorMessage from '../../components/modalErrorMessage/modalErrorMessage';
import { useProfessionals } from '../../hooks/useProfessionals';
import { useSavePersonalEvent } from '../hooks/useSavePersonalEvent';
import { useDeletePersonalEvent } from '../hooks/useDeletePersonalEvent';
import { parsePersonalEventDateTime } from './toPersonalEvent';

const NO_PROFESSIONAL_OPTION = '';

// Ids podem vir como número da API e como string do sessionStorage/filtro: a
// comparação frouxa evita um Select com valor que não casa com nenhuma opção.
const findProfessionalId = (professionals, candidateId) => {
  if (candidateId === null || candidateId === undefined || candidateId === '') return null;
  const found = professionals.find(
    (professional) => String(professional.id) === String(candidateId),
  );
  return found ? found.id : null;
};

function PersonalEventModal({
  open,
  onClose,
  onSave,
  onDelete,
  personalEvent,
  defaultDate,
  defaultProfessionalId,
}) {
  const navigate = useNavigate();
  const { professionals } = useProfessionals();
  const { save: savePersonalEventRequest } = useSavePersonalEvent();
  const { remove: deletePersonalEventRequest } = useDeletePersonalEvent();

  const [isLoading, setLoading] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isDeleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const [professionalId, setProfessionalId] = useState(NO_PROFESSIONAL_OPTION);
  const [date, setDate] = useState(dayjs());
  const [startTime, setStartTime] = useState(dayjs('08:00', 'HH:mm'));
  const [endTime, setEndTime] = useState(dayjs('09:00', 'HH:mm'));
  const [description, setDescription] = useState('');

  useEffect(() => {
    if (open) {
      const start = parsePersonalEventDateTime(personalEvent?.date, personalEvent?.startTime);
      const end = parsePersonalEventDateTime(personalEvent?.date, personalEvent?.endTime);

      // Na criação, o profissional já vem resolvido: o do filtro da agenda ou,
      // na falta dele, o profissional vinculado ao usuário logado.
      setProfessionalId(
        findProfessionalId(professionals, personalEvent?.professionalId) ??
          findProfessionalId(professionals, defaultProfessionalId) ??
          findProfessionalId(professionals, sessionStorage.getItem('dentistIdFromUser')) ??
          NO_PROFESSIONAL_OPTION,
      );
      setDate(start ?? (defaultDate ? dayjs(defaultDate) : dayjs()));
      setStartTime(start ?? dayjs('08:00', 'HH:mm'));
      setEndTime(end ?? dayjs('09:00', 'HH:mm'));
      setDescription(personalEvent?.description ?? '');
      setShowErrorMessage(false);
      setErrorMessage('');
      setDeleteConfirmOpen(false);
    }
  }, [personalEvent, defaultDate, defaultProfessionalId, professionals, open]);

  const savePersonalEvent = () => {
    if (!professionalId) {
      setShowErrorMessage(true);
      setErrorMessage('Selecionar o profissional é obrigatório.');
      return;
    }

    if (!date?.isValid()) {
      setShowErrorMessage(true);
      setErrorMessage('Selecionar a data do compromisso é obrigatório.');
      return;
    }

    if (!startTime?.isValid() || !endTime?.isValid()) {
      setShowErrorMessage(true);
      setErrorMessage('Informe a hora inicial e a hora final do compromisso.');
      return;
    }

    if (!endTime.isAfter(startTime, 'minute')) {
      setShowErrorMessage(true);
      setErrorMessage('A hora final deve ser maior que a hora inicial.');
      return;
    }

    if (!description.trim()) {
      setShowErrorMessage(true);
      setErrorMessage('Informe a descrição do compromisso.');
      return;
    }

    const input = {
      id: personalEvent?.id,
      professionalId,
      date: dayjs(date).format('DD/MM/YYYY'),
      startTime: startTime.format('HH:mm'),
      endTime: endTime.format('HH:mm'),
      description: description.trim(),
    };

    setLoading(true);
    savePersonalEventRequest(input)
      .then((returnFromApi) => {
        setLoading(false);
        onClose();
        onSave(returnFromApi);
      })
      .catch((returnFromApi) => {
        setLoading(false);
        setShowErrorMessage(true);
        setErrorMessage(String(returnFromApi.response?.data ?? 'Erro ao salvar o compromisso.'));
        console.error('ops! ocorreu um erro' + returnFromApi);
        if (returnFromApi.status === 401) {
          navigate('/expired-session');
        }
      });
  };

  const handleConfirmDelete = () => {
    setLoading(true);
    deletePersonalEventRequest(personalEvent.id)
      .then((returnFromApi) => {
        setLoading(false);
        setDeleteConfirmOpen(false);
        onClose();
        onDelete(returnFromApi);
      })
      .catch((returnFromApi) => {
        setLoading(false);
        setDeleteConfirmOpen(false);
        setShowErrorMessage(true);
        setErrorMessage(String(returnFromApi.response?.data ?? 'Erro ao excluir o compromisso.'));
        console.error('ops! ocorreu um erro' + returnFromApi);
        if (returnFromApi.status === 401) {
          navigate('/expired-session');
        }
      });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 'bold' }}>
        {personalEvent?.id ? 'Editar Compromisso Pessoal' : 'Novo Compromisso Pessoal'}
      </DialogTitle>
      <DialogContent>
        <Loading isToHidden={isLoading === false} />
        {showErrorMessage && (
          <ModalErrorMessage message={errorMessage} setShow={setShowErrorMessage} />
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
          Reserve um horário na sua agenda para um compromisso que não é uma consulta (reunião,
          médico, curso). Ele aparece no calendário, mas não envolve paciente.
        </Typography>

        <Stack spacing={2}>
          <TextField
            id="professional"
            select
            label="Profissional"
            fullWidth
            value={professionalId}
            onChange={(e) => setProfessionalId(e.target.value)}
          >
            {professionals.map((professional) => (
              <MenuItem key={professional.id} value={professional.id}>
                {professional.nome || professional.name}
              </MenuItem>
            ))}
          </TextField>

          <DatePicker
            label="Data"
            value={date}
            onChange={(value) => setDate(value)}
            format="DD/MM/YYYY"
            slotProps={{ textField: { fullWidth: true } }}
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TimeField
              label="Hora Inicial"
              value={startTime}
              format="HH:mm"
              onChange={(value) => setStartTime(value)}
              fullWidth
            />
            <TimeField
              label="Hora Final"
              value={endTime}
              format="HH:mm"
              onChange={(value) => setEndTime(value)}
              fullWidth
            />
          </Stack>

          <TextField
            id="description"
            label="Descrição"
            type="text"
            fullWidth
            multiline
            minRows={2}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Ex.: Reunião com o contador, Consulta médica, Curso"
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: '0 24px 16px' }}>
        {personalEvent?.id && (
          <Button
            onClick={() => setDeleteConfirmOpen(true)}
            color="error"
            startIcon={<DeleteOutlineIcon />}
            sx={{ mr: 'auto' }}
          >
            Excluir
          </Button>
        )}
        <Button onClick={onClose}>Cancelar</Button>
        <Button onClick={savePersonalEvent} variant="contained" color="secondary">
          Salvar
        </Button>
      </DialogActions>

      <Dialog
        open={isDeleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Excluir compromisso pessoal</DialogTitle>
        <DialogContent dividers>
          <Typography>
            O compromisso sairá da agenda e o horário voltará a ficar livre. Deseja continuar?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleConfirmDelete}>
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}

PersonalEventModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
  personalEvent: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    professionalId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    date: PropTypes.string,
    startTime: PropTypes.string,
    endTime: PropTypes.string,
    description: PropTypes.string,
  }),
  defaultDate: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  defaultProfessionalId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

PersonalEventModal.defaultProps = {
  onDelete: () => {},
  personalEvent: null,
  defaultDate: null,
  defaultProfessionalId: null,
};

export default PersonalEventModal;
